import React, { useState, useEffect } from "react";
import { Box, Group, Text, Switch, Loader, Stack, Paper, Title, Divider, Button, Modal, Textarea, ActionIcon, Tooltip } from "@mantine/core";
import { useAuth0 } from "@auth0/auth0-react";
import { showNotification } from "@mantine/notifications";
import { getNotificationSettings, updateNotificationSettings } from "../../Api";
import { IconEdit, IconEye } from "@tabler/icons";

interface NotificationSetting {
  submitter?: boolean;
  owner?: boolean;
  ideaOwner?: boolean;
  template?: string;
  description: string;
}

interface NotificationSettings {
  _id?: string;
  accountId?: string;
  type?: string;
  name?: string;
  "idea-status-change": NotificationSetting;
  "idea-owner-change": NotificationSetting;
  "idea-deleted-archived": NotificationSetting;
  "RequestAttached": NotificationSetting;
  "RequestValidated": NotificationSetting;
  "RequestNewComment": NotificationSetting;
  "RequestDeclined": NotificationSetting;
  "RequestReassign": NotificationSetting;
  createdAt: string;
  updatedAt: string;
}

const defaultNotificationSettings: NotificationSettings = {
  "idea-status-change": {
    submitter: true,
    description: "When the status of an idea changes"
  },
  "idea-owner-change": {
    owner: true,
    description: "When the owner of an idea changes"
  },
  "idea-deleted-archived": {
    submitter: true,
    description: "When an idea is deleted or archived"
  },
  "RequestAttached": {
    submitter: true,
    description: "When a request is attached to an idea"
  },
  "RequestValidated": {
    owner: true,
    description: "When a request is validated"
  },    
  "RequestNewComment": {
    submitter: true,
    owner: true,
    ideaOwner: true,
    description: "When a new comment is added to a request"
  },    
  "RequestDeclined": {
    submitter: true,
    description: "When a request is declined"
  },
  "RequestReassign": {
    owner: true,
    description: "When a request is reassigned"
  },    
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  type: "configuration",
  name: "notification-settings"
};
const Notifications: React.FC = () => {
  const auth0 = useAuth0();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [settings, setSettings] = useState<NotificationSettings | null>(defaultNotificationSettings);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Template editor state
  const [templateModalOpened, setTemplateModalOpened] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<{settingKey: string, template: string} | null>(null);
  const [templateValue, setTemplateValue] = useState("");

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setIsLoading(true);
        const data = await getNotificationSettings(auth0, {
          type: "configuration",
          name: "notification-settings"
        });
        setSettings(data || defaultNotificationSettings);
      } catch (error) {
        showNotification({
          title: "Error",
          message: "Failed to load notification settings",
          color: "red",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchSettings();
  }, [auth0]);

  const handleToggle = (settingKey: string, role: 'submitter' | 'owner' | 'ideaOwner') => {
    if (!settings) return;

    setSettings(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        [settingKey]: {
          ...prev[settingKey as keyof NotificationSettings] as NotificationSetting,
          [role]: !(prev[settingKey as keyof NotificationSettings] as NotificationSetting)?.[role]
        }
      };
    });
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!settings || !hasChanges) return;

    try {
      setIsSaving(true);
      await updateNotificationSettings(auth0, settings);
      showNotification({
        title: "Success",
        message: "Notification settings updated successfully",
        color: "green",
      });
      setHasChanges(false);
    } catch (error) {
      showNotification({
        title: "Error",
        message: "Failed to update notification settings",
        color: "red",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Template editing functions
  const handleEditTemplate = (settingKey: string, currentTemplate = "") => {
    setEditingTemplate({ settingKey, template: currentTemplate });
    setTemplateValue(currentTemplate);
    setTemplateModalOpened(true);
  };

  const handleSaveTemplate = () => {
    if (!settings || !editingTemplate) return;

    setSettings(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        [editingTemplate.settingKey]: {
          ...prev[editingTemplate.settingKey as keyof NotificationSettings] as NotificationSetting,
          template: templateValue
        }
      };
    });
    setHasChanges(true);
    setTemplateModalOpened(false);
    setEditingTemplate(null);
    setTemplateValue("");

    showNotification({
      title: "Success",
      message: "Template updated successfully",
      color: "green",
    });
  };

  const handleCancelTemplate = () => {
    setTemplateModalOpened(false);
    setEditingTemplate(null);
    setTemplateValue("");
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
        <Loader />
      </Box>
    );
  }

  const renderSettingToggles = (settingKey: string, setting: NotificationSetting) => {
    // Convert setting key to a more readable format
    const readableKey = settingKey
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

    return (
      <Paper p="md" withBorder key={settingKey}>
        <Stack spacing="xs">
          <Group position="apart" spacing="xs">
            <Group spacing="xs">
              <Text weight={700}>{readableKey}</Text>
              <Text size="sm" color="dimmed">({setting.description})</Text>
            </Group>
            <Group spacing="xs">
              <Tooltip label="Edit Template">
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  onClick={() => handleEditTemplate(settingKey, setting.template)}
                >
                  <IconEdit size={16} />
                </ActionIcon>
              </Tooltip>
            </Group>
          </Group>
          <Group spacing="xl">
            {setting.submitter !== undefined && (
              <Group spacing="xs">
                <Switch
                  checked={setting.submitter}
                  onChange={() => handleToggle(settingKey, 'submitter')}
                  label="Submitter"
                />
              </Group>
            )}
            {setting.owner !== undefined && (
              <Group spacing="xs">
                <Switch
                  checked={setting.owner}
                  onChange={() => handleToggle(settingKey, 'owner')}
                  label="Owner"
                />
              </Group>
            )}
            {setting.ideaOwner !== undefined && (
              <Group spacing="xs">
                <Switch
                  checked={setting.ideaOwner}
                  onChange={() => handleToggle(settingKey, 'ideaOwner')}
                  label="Idea Owner"
                />
              </Group>
            )}
          </Group>
        </Stack>
      </Paper>
    );
  };

  return (
    <Box sx={{ padding: "15px" }}>
      <Group position="apart" mb="md">
        <Title order={2}>Notification Settings</Title>
        <Button
          onClick={handleSave}
          loading={isSaving}
          disabled={!hasChanges}
        >
          Save Changes
        </Button>
      </Group>
      {settings && (
        <Stack spacing="md">
          {Object.entries(settings).map(([key, value]) => {
            if (key === '_id' || key === 'accountId' || key === 'type' || key === 'name' || key === 'createdAt' || key === 'updatedAt') {
              return null;
            }
            return renderSettingToggles(key, value as NotificationSetting);
          })}
        </Stack>
      )}

      {/* Template Editor Modal */}
      <Modal
        opened={templateModalOpened}
        onClose={handleCancelTemplate}
        title={`Edit Template - ${editingTemplate?.settingKey ? editingTemplate.settingKey.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') : ''}`}
        size="lg"
      >
        <Stack spacing="md">
          {/* <Text size="sm" color="dimmed">
            Edit the notification template. You can use variables like {"{ideaName}"}, {"{userName}"}, {"{status}"}, etc.
          </Text> */}
          <Textarea
            value={templateValue}
            onChange={(event) => setTemplateValue(event.currentTarget.value)}
            placeholder="Enter your notification template here..."
            minRows={8}
            maxRows={12}
            autosize
          />
          <Group position="right" spacing="md">
            <Button variant="outline" onClick={handleCancelTemplate}>
              Cancel
            </Button>
            <Button onClick={handleSaveTemplate}>
              Save Template
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  );
};

export default Notifications;
